import { axiosInstance } from './api';

/**
 * Microphone (audio capture device) REST client.
 *
 * Mirrors the backend model in
 * `services/plugins/equipment_discovery/models.py:Microphone` + the
 * endpoints in `services/plugins/equipment_discovery/api/microphones.py`.
 *
 * Unlike NVR/Camera a microphone is not a LAN host — it's a USB/ALSA
 * capture device on the zone's Jetson, identified by its ALSA address
 * (`hw:<card>,<device>`). `discover` runs an `arecord -l` self-probe on
 * the Jetson and returns the available devices; the operator picks one
 * and POSTs it. The record gates the speech-analytics tab via
 * `Zone.has_mic`.
 *
 * Сетевой микрофон (роутер у кассы точки без джетсона) — та же запись с
 * `alsa_device = tcp://<wg-ip>:5555`, `kind: 'network'`; его пишет централь,
 * `check` записывает несколько секунд с роутера и отдаёт уровни + клип.
 */

/** `network` — роутер с микрофоном (`tcp://…`), `usb` — микрофон в джетсоне. */
export type MicrophoneKind = 'network' | 'usb';

export interface MicrophoneRecord {
  id: number;
  zone_id: number;
  kind: MicrophoneKind;
  local_computer_id: string | null;
  alsa_device: string;
  card_index: number | null;
  device_index: number | null;
  card_id: string;
  card_name: string;
  sample_rate: number;
  channels: number;
  enabled: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

/** Writable fields on POST (`alsa_device` is required) / PATCH. */
export type MicrophoneUpsert = Partial<
  Pick<
    MicrophoneRecord,
    | 'alsa_device'
    | 'card_index'
    | 'device_index'
    | 'card_id'
    | 'card_name'
    | 'sample_rate'
    | 'channels'
    | 'enabled'
    | 'notes'
  >
>;

/** One capture device returned by the Jetson `arecord -l` self-probe. */
export interface DiscoveredAudioDevice {
  alsa_device: string;
  card_index: number | null;
  card_id: string;
  card_name: string;
  device_index: number | null;
  device_name: string;
}

/** Ответ `check`: `online: null` — для USB-микрофона проверка не делается. */
export interface MicrophoneCheckResult {
  online: boolean | null;
  kind: MicrophoneKind;
  seconds?: number;
  rms_db?: number | null;
  peak_db?: number | null;
  audio_b64?: string | null;
  audio_mime?: string;
  error: string | null;
}

const zoneUrl = (zoneId: number | string) =>
  `/equipment_discovery/zones/${zoneId}/microphones/`;
const micUrl = (micId: number | string) =>
  `/equipment_discovery/microphones/${micId}/`;
const discoverUrl = (zoneId: number | string) =>
  `/equipment_discovery/zones/${zoneId}/microphones/discover/`;

interface ZoneMicrophonesResponse {
  results: MicrophoneRecord[];
}

interface DiscoverMicrophonesResponse {
  devices: DiscoveredAudioDevice[];
}

export const listZoneMicrophones = (zoneId: number | string) =>
  axiosInstance.get<ZoneMicrophonesResponse>(zoneUrl(zoneId));

export const createMicrophone = (zoneId: number | string, payload: MicrophoneUpsert) =>
  axiosInstance.post<MicrophoneRecord>(zoneUrl(zoneId), payload);

export const updateMicrophone = (micId: number | string, payload: MicrophoneUpsert) =>
  axiosInstance.patch<MicrophoneRecord>(micUrl(micId), payload);

export const deleteMicrophone = (micId: number | string) =>
  axiosInstance.delete<void>(micUrl(micId));

/** Jetson self-probe — lists the ALSA capture devices for this zone. */
export const discoverMicrophones = (zoneId: number | string) =>
  axiosInstance.post<DiscoverMicrophonesResponse>(discoverUrl(zoneId));

/** Сетевой микрофон: записать `seconds` (до 15) с роутера — уровни и клип. */
export const checkMicrophone = (micId: number | string, seconds?: number) =>
  axiosInstance.post<MicrophoneCheckResult>(
    `${micUrl(micId)}check/`,
    seconds ? { seconds } : {},
    // ffmpeg ждёт до seconds + 10 с, плюс mp3 — с запасом.
    { timeout: 60000 },
  );
